import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Home, 
  BookOpen, 
  Award, 
  Bell, 
  User, 
  Settings,
  Users,
  BarChart3,
  Folder,
  Mail,
  FileText,
  X
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { clsx } from 'clsx';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { user, isAdmin } = useAuth();
  const location = useLocation();

  // Navegación principal para usuarios
  const userNavigation = [
    // { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Categorías', href: '/home', icon: BookOpen },
    { name: 'Certificados', href: '/certificates', icon: Award },
    { name: 'Mis Notificaciones', href: '/notifications', icon: Bell },
    { name: 'Mi Perfil', href: '/profile', icon: User },
  ];

  // Navegación adicional para administradores
  const adminNavigation = [
    { name: 'Admin Dashboard', href: '/admin', icon: BarChart3 },
    { name: 'Gestión de Usuarios', href: '/admin/users', icon: Users },
    { name: 'Grupos', href: '/admin/groups', icon: Folder },
    { name: 'Contenido', href: '/admin/content', icon: FileText },
    { name: 'Gestión de Notificaciones', href: '/admin/notifications', icon: Mail },
    { name: 'Reportes', href: '/admin/reports', icon: BarChart3 },
  ];

  const navigation = isAdmin 
    ? [...userNavigation, ...adminNavigation]
    : userNavigation;

  return (
    <>
      {/* Sidebar para desktop */}
      <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0 lg:z-30">
        <SidebarContent 
          navigation={navigation} 
          user={user} 
          currentPath={location.pathname}
        />
      </div>

      {/* Sidebar para móvil - CORREGIDO */}
      <AnimatePresence>
        {isOpen && (
          <div className="lg:hidden">
            {/* Overlay con backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50"
              onClick={onClose}
            />
            
            {/* Sidebar container */}
            <div className="fixed inset-0 flex z-50">
              <motion.div
                initial={{ x: -320 }}
                animate={{ x: 0 }}
                exit={{ x: -320 }}
                transition={{ 
                  type: "spring", 
                  stiffness: 300, 
                  damping: 30,
                  duration: 0.3
                }}
                className="relative flex-1 flex flex-col max-w-xs w-full shadow-xl"
                onClick={(e) => e.stopPropagation()}
              >
                <SidebarContent 
                  navigation={navigation} 
                  user={user} 
                  currentPath={location.pathname}
                  onClose={onClose}
                  isMobile
                />
              </motion.div>
              
              {/* Área vacía clickeable para cerrar */}
              <div className="flex-shrink-0 w-14" onClick={onClose} />
            </div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

// Componente de contenido del sidebar - SIN CAMBIOS
interface SidebarContentProps {
  navigation: Array<{
    name: string;
    href: string;
    icon: React.ComponentType<any>;
  }>;
  user: any;
  currentPath: string;
  onClose?: () => void;
  isMobile?: boolean;
}

const SidebarContent: React.FC<SidebarContentProps> = ({ 
  navigation, 
  user, 
  currentPath, 
  onClose,
  isMobile = false 
}) => {
  return (
    <div className="flex flex-col flex-grow bg-white/95 backdrop-blur border-r border-slate-200 pt-5 pb-4 overflow-y-auto">
      {/* Logo y botón de cerrar */}
      <div className="flex items-center justify-between flex-shrink-0 px-4">
        <div className="flex items-center">
          <img
            className="h-8 w-auto"
            src="\assets\images\logo_azul.png"
            alt="Brinsa"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextElementSibling?.classList.remove('hidden');
            }}
          />
          <span className="hidden ml-2 text-xl font-bold text-brinsa-azul">
            Brinsa
          </span>
        </div>
        
        {isMobile && (
          <button
            type="button"
            className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brinsa-celeste hover:bg-slate-100 transition-colors"
            onClick={onClose}
          >
            <span className="sr-only">Cerrar sidebar</span>
            <X className="h-6 w-6 text-brinsa-azulOscuro" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Navegación */}
      <nav className="mt-5 flex-1 px-2 space-y-1">
        {navigation.map((item) => {
          const isActive = currentPath === item.href || 
                          (item.href !== '/dashboard' && currentPath.startsWith(item.href));
          
          return (
            <NavLink
              key={item.name}
              to={item.href}
              onClick={isMobile ? onClose : undefined}
              className={({ isActive: navIsActive }) => clsx(
                'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors border-r-2 border-transparent',
                (isActive || navIsActive)
                  ? 'bg-brinsa-celeste/15 text-brinsa-azul border-r-brinsa-azulClaro'
                  : 'text-brinsa-azulOscuro/80 hover:bg-brinsa-celeste/10 hover:text-brinsa-azul'
              )}
            >
              <item.icon
                className={clsx(
                  'mr-3 flex-shrink-0 h-5 w-5',
                  isActive
                    ? 'text-brinsa-azul'
                    : 'text-brinsa-azulOscuro/60 group-hover:text-brinsa-azul'
                )}
                aria-hidden="true"
              />
              {item.name}
              
              {/* Indicador de sección admin */}
              {item.href.startsWith('/admin') && (
                <span className="ml-auto">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-brinsa-azul/10 text-brinsa-azul border border-brinsa-azul/20">
                    Admin
                  </span>
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Información del usuario */}
      <div className="mt-5 px-4">
        <div className="bg-brinsa-celeste/10 rounded-lg p-3 border border-brinsa-celeste/20">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-brinsa-azul rounded-full flex items-center justify-center">
              <span className="text-white font-medium">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="ml-3 flex-1 min-w-0">
              <p className="text-sm font-medium text-brinsa-azul truncate">
                {user?.name}
              </p>
              <p className="text-xs text-brinsa-azulOscuro/70 truncate">
                {user?.role === 'admin' ? 'Administrador' : 'Usuario'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Pie del sidebar */}
      <div className="flex-shrink-0 p-4 border-t mt-6 border-slate-200">
        <div className="text-xs text-brinsa-azulOscuro/60 text-center">
          <p>Universidad BrinsAprende</p>
          <p className="mt-1">v1.0.0</p>
          <p className="mt-1 text-[8px]">© 2025 DevLight. All rights reserved. </p>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;